import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { NotificationChannel, NotificationStatus, PageRequest } from "./types";

export interface NotificationOutboxFilter {
  status?: NotificationStatus | undefined;
  channel?: NotificationChannel | undefined;
  topic?: string | undefined;
  memberId?: string | undefined;
}

/**
 * The notification engine, staff side (`/notifications/*`): the signed-in staff member's own bell and preferences, and for
 * `notifications.manage` the outbox (who was told what topic on which channel and how it went; never the text).
 */
export function notificationsEndpoints({ get, post, patch, put, del, paged }: Helpers) {
  return {
    inbox: (page: PageRequest & { unreadOnly?: boolean | undefined } = {}) =>
      get<Out<"notifications_staff_inbox">>("/notifications/inbox", paged(page, { unread_only: page.unreadOnly })),
    unread: (signal?: AbortSignal) => get<Out<"notifications_staff_unread">>("/notifications/inbox/unread", { signal }),
    /** Mark entries read; leave `ids` out to mark all of them. */
    markRead: (input: In<"notifications_staff_mark_read">) => post<Out<"notifications_staff_mark_read">>("/notifications/inbox/read", input),
    preferences: (signal?: AbortSignal) => get<Out<"notifications_staff_preferences">>("/notifications/preferences", { signal }),
    updatePreferences: (input: In<"notifications_staff_update_preferences">) =>
      patch<Out<"notifications_staff_update_preferences">>("/notifications/preferences", input),

    outbox: (page: PageRequest & NotificationOutboxFilter = {}) =>
      get<Out<"notifications_outbox">>("/notifications/outbox", paged(page, { status: page.status, channel: page.channel, topic: page.topic, member_id: page.memberId })),
    outboxSummary: (signal?: AbortSignal) => get<Out<"notifications_outbox_summary">>("/notifications/outbox/summary", { signal }),
    retry: (id: string) => post<Out<"notifications_retry_message">>(`/notifications/outbox/${id}/retry`),
    cancel: (id: string) => post<Out<"notifications_cancel_message">>(`/notifications/outbox/${id}/cancel`),

    /** Every message's wording per channel and language (the club's own or the built-in one), notifications and the emails sent at once. */
    templates: (signal?: AbortSignal) => get<Out<"notifications_list_templates">>("/notifications/templates", { signal }),
    saveTemplate: (topic: string, channel: string, language: string, input: In<"notifications_save_template">) =>
      put<Out<"notifications_save_template">>(`/notifications/templates/${topic}/${channel}/${language}`, input),
    /** Back to the built-in wording. */
    resetTemplate: (topic: string, channel: string, language: string) =>
      del<Out<"notifications_reset_template">>(`/notifications/templates/${topic}/${channel}/${language}`),
    /** A draft filled in with sample values, and anything that would stop it being saved. Saves nothing. */
    previewTemplate: (input: In<"notifications_preview_template">) => post<Out<"notifications_preview_template">>("/notifications/templates/preview", input),
  };
}
